import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiCheck, FiCopy, FiDownloadCloud, FiLink, FiX } from "react-icons/fi";
import { downloadUrl } from "../../../../config/BaseUrl";
import "./MediaDownloadPublicLink.css";

const MediaDownloadPublicLink = ({ modalId, selectedUuid }) => {
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef(null);
  const publicLink = useMemo(
    () => `${downloadUrl}file/download?type=all&event_id=${selectedUuid || ""}`,
    [selectedUuid]
  );

  useEffect(() => {
    const modalElement = document.getElementById(modalId);
    const handleModalOpen = () => setCopied(false);

    modalElement?.addEventListener("show.bs.modal", handleModalOpen);

    return () => {
      modalElement?.removeEventListener("show.bs.modal", handleModalOpen);
      clearTimeout(copiedTimer.current);
    };
  }, [modalId]);

  const copyWithFallback = () => {
    const textArea = document.createElement("textarea");
    textArea.value = publicLink;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  };

  const copyDownloadLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(publicLink);
      } else {
        copyWithFallback();
      }

      setCopied(true);
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      copyWithFallback();
      setCopied(true);
    }
  };

  return (
    <div className="modal fade gallery-link-modal" id={modalId} tabIndex="-1" role="dialog" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <div className="gallery-link-heading">
              <span className="gallery-link-heading-icon"><FiLink /></span>
              <div>
                <span>Share gallery</span>
                <h5 className="modal-title">Event Gallery Link</h5>
              </div>
            </div>
            <button type="button" className="gallery-link-close" data-dismiss="modal" aria-label="Close">
              <FiX />
            </button>
          </div>

          <div className="modal-body">
            <div className="gallery-link-intro">
              <FiDownloadCloud />
              <div>
                <h6>Download all event media</h6>
                <p>Anyone with this link can download the photos, videos and animations from this event.</p>
              </div>
            </div>

            <label className="gallery-link-label" htmlFor={`${modalId}-link`}>Public download link</label>
            <div className="gallery-link-field">
              <FiLink aria-hidden="true" />
              <input id={`${modalId}-link`} type="text" readOnly value={publicLink} onFocus={(event) => event.target.select()} />
              <button type="button" onClick={copyDownloadLink} disabled={!selectedUuid}>
                {copied ? <FiCheck /> : <FiCopy />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>

            <div className={`gallery-link-feedback ${copied ? "is-visible" : ""}`} aria-live="polite">
              {copied ? <><FiCheck /> Link copied to your clipboard</> : null}
            </div>
          </div>

          <div className="modal-footer">
            <span><FiLink /> Ready to share</span>
            <div>
              <button
                type="button"
                id={`close-${modalId}`}
                className="gallery-link-cancel"
                data-dismiss="modal"
                aria-label="Close"
              >
                Close
              </button>
              <button type="button" onClick={copyDownloadLink} className="gallery-link-copy" disabled={!selectedUuid}>
                {copied ? <FiCheck /> : <FiCopy />}
                {copied ? "Copied" : "Copy link"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaDownloadPublicLink;
